import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PeriodeData } from 'src/app/class/datatype';
import { MONTH_NAMES } from 'src/app/views/cashier/cashier.constants';
import { CashierService } from 'src/app/views/cashier/cashier.service';
import { CashierStateService } from 'src/app/views/cashier/cashier-state.service';

/**
 * Pilihan periode layar kasir, ditaruh di header di samping tanda mode. Periode dipilih sekali
 * dan dikunci; mengubahnya harus lewat tombol "Ganti". Di halaman pembayaran periode hanya
 * tampil (tidak bisa diganti) supaya transaksi yang sedang berjalan tidak ikut berpindah periode.
 */
@Component({
  selector: 'app-cashier-periode',
  templateUrl: './app-cashier-periode.component.html',
  styleUrls: ['./app-cashier-periode.component.scss'],
})
export class AppCashierPeriodeComponent implements OnInit, OnDestroy {
  periodes: PeriodeData[] = [];
  /** Isi dropdown; baru berlaku sebagai periode kasir setelah dikonfirmasi. */
  selectedPeriodeId: number | null = null;
  /** Periode yang sedang dipakai kasir; null sampai dipilih. */
  active: PeriodeData | null = null;
  editing = false;
  loading = true;
  errorMessage = '';
  /** Halaman pembayaran: periode tampil saja. */
  readonly = false;

  private readonly subscriptions = new Subscription();

  constructor(
    private cashier: CashierService,
    private state: CashierStateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.state.sync();
    this.updateReadonly(this.router.url);
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => this.updateReadonly(event.urlAfterRedirects))
    );

    this.cashier.listPeriode().subscribe({
      next: (result) => {
        this.periodes = result.result ?? [];
        this.restore();
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.errorMessage =
          error.status === 403
            ? 'Tidak punya izin akses data periode.'
            : error.error?.message || 'Gagal memuat periode.';
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get selected(): PeriodeData | undefined {
    return this.periodes.find((p) => p.id === this.selectedPeriodeId);
  }

  label(periode: PeriodeData): string {
    return `${periode.date} ${MONTH_NAMES[periode.month - 1]}`;
  }

  /** Mengunci periode yang dipilih di dropdown. */
  confirm(): void {
    const periode = this.selected;
    if (!periode) {
      return;
    }

    this.active = periode;
    this.state.periode = periode;
    this.editing = false;
  }

  /** Membuka dropdown lagi, dipakai tombol "Ganti". */
  change(): void {
    this.selectedPeriodeId = this.active?.id ?? null;
    this.editing = true;
  }

  /** Menutup dropdown tanpa mengubah periode yang sedang dipakai. */
  cancel(): void {
    this.selectedPeriodeId = this.active?.id ?? null;
    this.editing = false;
  }

  /**
   * Periode terakhir yang dipilih dipakai lagi tanpa bertanya (bila masih aktif). Kalau belum ada
   * atau sudah tidak aktif, kasir diminta memilih; dropdown sudah terisi usulan (periode bulan ini
   * dengan tanggal sesudah hari ini, sama dengan Cashpay lama, lalu yang pertama).
   */
  private restore(): void {
    const saved = this.state.periode;
    const current = saved ? this.periodes.find((p) => p.id === saved.id) : undefined;
    if (current) {
      this.active = current;
      this.selectedPeriodeId = current.id ?? null;
      this.editing = false;
      return;
    }

    if (saved) {
      this.state.periode = null;
    }
    this.active = null;
    this.selectedPeriodeId = this.suggestedId();
    this.editing = true;
  }

  private suggestedId(): number | null {
    const today = this.today();
    const upcoming = this.periodes.find(
      (p) => p.month === today.getMonth() + 1 && p.date > today.getDate()
    );

    return upcoming?.id ?? this.periodes[0]?.id ?? null;
  }

  /** Hari ini; dipisah supaya bisa diatur di tes. */
  protected today(): Date {
    return new Date();
  }

  private updateReadonly(url: string): void {
    this.readonly = url.split('?')[0].endsWith('/cashier/pay');
  }
}
